import apiRequest from "./requests";
import { synchronize } from "@nozbe/watermelondb/sync";
import { database } from "..";
import { useEffect, useState } from "react";

export type SyncState = {
  status: "idle" | "syncing" | "offline" | "error";
  lastSyncedAt?: number;
};

let state: SyncState = { status: "idle" };
const listeners = new Set<(s: SyncState) => void>();

function setState(update: Partial<SyncState>) {
  state = { ...state, ...update };
  listeners.forEach((l) => l(state));
}

// Current sync status, for the indicator in the top bar.
export function useSyncState(): SyncState {
  const [s, setS] = useState(state);
  useEffect(() => {
    listeners.add(setS);
    return () => {
      listeners.delete(setS);
    };
  }, []);
  return s;
}

// Only one sync runs at a time. A slow sync (the first one on a device
// downloads every photo) would otherwise overlap the next one, which the
// database rejects. A request made while one runs queues one more run, so
// changes made during a sync are never left behind.
let running: Promise<void> | null = null;
let again = false;

export function syncDatabase(token: string): Promise<void> {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    do {
      again = false;
      await runSync(token);
    } while (again);
  })().finally(() => {
    running = null;
  });
  return running;
}

export function isSyncing() {
  return running !== null;
}

async function runSync(token: string) {
  if (!navigator.onLine) {
    setState({ status: "offline" });
    throw new Error("Offline");
  }
  setState({ status: "syncing" });
  try {
    await synchronize({
      database,
      pullChanges: async ({ lastPulledAt, schemaVersion, migration }) => {
        const urlParams = `last_pulled_at=${lastPulledAt}&schema_version=${schemaVersion}&migration=${encodeURIComponent(
          JSON.stringify(migration)
        )}`;
        try {
          const response = await apiRequest.get(`/sync/pull?${urlParams}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const { changes, timestamp } = await response.data;
          return { changes, timestamp };
        } catch (e: any) {
          if (e.response?.status === 401) {
            localStorage.removeItem("token");
            window.location.reload();
          }
        }
        throw new Error("Sync Error");
      },
      pushChanges: async ({ changes, lastPulledAt }) => {
        await apiRequest.post(
          `/sync/push?last_pulled_at=${lastPulledAt}`,
          changes,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      },
    });
    setState({ status: "idle", lastSyncedAt: Date.now() });
  } catch (e) {
    setState({ status: navigator.onLine ? "error" : "offline" });
    throw e;
  }
}

const SYNCED_TABLES = [
  "customers",
  "photos",
  "taxes",
  "products",
  "proposals",
  "proposal_line_items",
  "settings",
  "email_templates",
  "emails",
  "appointments",
  "sms_messages",
];

// Keeps the device in sync without anyone pressing a button: shortly after
// any change, as soon as the connection comes back, when the app is brought
// back to the front, and every 30 seconds. Returns a function that stops it.
export function startAutoSync(): () => void {
  const sync = () => {
    const token = localStorage.getItem("token");
    if (token) {
      // Failures are shown by the indicator and retried on the next trigger.
      syncDatabase(token).catch(() => {});
    }
  };

  let changeTimer: ReturnType<typeof setTimeout> | undefined;
  const subscription = database
    .withChangesForTables(SYNCED_TABLES)
    .subscribe(() => {
      // Changes written by a sync itself don't need another sync.
      if (isSyncing()) {
        return;
      }
      // Typing saves on every keystroke; wait for a pause.
      clearTimeout(changeTimer);
      changeTimer = setTimeout(sync, 2000);
    });

  const onVisible = () => {
    if (document.visibilityState === "visible") {
      sync();
    }
  };
  const onOffline = () => setState({ status: "offline" });

  window.addEventListener("online", sync);
  window.addEventListener("offline", onOffline);
  document.addEventListener("visibilitychange", onVisible);
  const interval = setInterval(sync, 30000);
  sync();

  return () => {
    subscription.unsubscribe();
    clearTimeout(changeTimer);
    clearInterval(interval);
    window.removeEventListener("online", sync);
    window.removeEventListener("offline", onOffline);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
